import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useRoutes } from 'react-router-dom';

import DashboardLayout from 'src/layouts/overview';
import TagSearchPage from 'src/pages/tag-search';
import AuthGuard from './guards/auth-guard';
import GuestGuard from './guards/guest-guard';

export const IndexPage = lazy(() => import('src/pages/overview'));
export const BlogPage = lazy(() => import('src/pages/spotlight'));
export const OrganisationStats = lazy(() => import('src/pages/organisation-stats'));
export const HostStatsPage = lazy(() => import('src/pages/host-stats'));
export const LoginPage = lazy(() => import('src/pages/login'));
export const FfufOutputPage = lazy(() => import('src/pages/ffuf-output'));
export const FfufInputPage = lazy(() => import('src/pages/ffuf-input'));
export const Page404 = lazy(() => import('src/pages/page-not-found'));
export const DomainLiveStream = lazy(() => import('src/pages/domain-live-stream'));
export const NewEndpoints = lazy(() => import('src/pages/new-endpoints'));
export const AclBypassesPage = lazy(() => import('src/pages/acl-bypasses'));
export const PortLiveStream = lazy(() => import('src/pages/port-live-stream'));
export const TitleResults = lazy(() => import('src/pages/new-titles'));
export const RelatedDomainsPage = lazy(() => import('src/pages/related-domains'));
export const NucleiScanPage = lazy(() => import('src/pages/nuclei-scan'));
export const RegressionTrackerPage = lazy(() => import('src/pages/regression-tracker'));
export const VulnerabilityFindingsPage = lazy(() => import('src/pages/vulnerability-findings'));
export const VhostLiveStreamPage = lazy(() => import('src/pages/vhost-live-stream'));
export const IntegrationsPage = lazy(() => import('src/pages/integrations'));
export const FindingsImportPage = lazy(() => import('src/pages/findings-import'));
export const CommandCenterPage = lazy(() => import('src/pages/command-center'));
export const SecretsLiveStreamPage = lazy(() => import('src/pages/secrets-live-stream'));
export const MemoryBankPage = lazy(() => import('src/pages/memory-bank'));
export const DeckViewerPage = lazy(() => import('src/pages/DeckViewerPage'));
export const ResultsAnalysisPage = lazy(() => import('src/pages/results-analysis'));
export const AiSummariesPage = lazy(() => import('src/pages/ai-summaries'));
export const TeamAssetOwnershipPage = lazy(() => import('src/pages/team-asset-ownership'));
export const ValidationWorkflowPage = lazy(() => import('src/pages/validation-workflow'));
export const RiskScoringConfigPage = lazy(() => import('src/pages/risk-scoring-config'));
export const OrganizationalContextConfigPage = lazy(() => import('src/pages/organizational-context-config'));
export const DomainLeadsPage = lazy(() => import('src/pages/domain-leads'));

// ----------------------------------------------------------------------

export default function Router() {
  const routes = useRoutes([
    {
      path: 'login',
      element: (
        <GuestGuard>
          <LoginPage />
        </GuestGuard>
      ),
    },
    {
      element: (
        <AuthGuard>
          <DashboardLayout>
            <Suspense>
              <Outlet />
            </Suspense>
          </DashboardLayout>
        </AuthGuard>
      ),
      children: [
        { element: <IndexPage />, index: true },
        { path: 'overview', element: <IndexPage /> },
        { path: 'organisation-stats', element: <OrganisationStats /> },
        { path: 'ffuf-output', element: <FfufOutputPage /> },
        { path: 'ffuf-input', element: <FfufInputPage /> },
        { path: 'spotlight', element: <BlogPage /> },
        { path: 'domain-live-stream', element: <DomainLiveStream /> },
        { path: 'vulnerability-findings', element: <VulnerabilityFindingsPage /> },
        { path: 'vhost-live-stream', element: <VhostLiveStreamPage /> },
        { path: 'acl-bypasses', element: <AclBypassesPage /> },
        { path: 'team-asset-ownership', element: <TeamAssetOwnershipPage /> },
        { path: 'new-endpoints', element: <NewEndpoints /> },
        { path: 'port-domain-live-stream', element: <PortLiveStream /> },
        { path: 'host-stats', element: <HostStatsPage /> },
        { path: 'new-titles', element: <TitleResults /> },
        { path: 'related-domains', element: <RelatedDomainsPage /> },
        { path: 'tag-search', element: <TagSearchPage /> },
        { path: 'nuclei-scan', element: <NucleiScanPage /> },
        { path: 'regression-tracker', element: <RegressionTrackerPage /> },
        { path: 'integrations', element: <IntegrationsPage /> },
        { path: 'command-center', element: <CommandCenterPage /> },
        { path: 'findings-import', element: <FindingsImportPage /> },
        { path: 'secrets-live-stream', element: <SecretsLiveStreamPage /> },
        { path: 'memory-bank', element: <MemoryBankPage /> },
        { path: 'results-analysis', element: <ResultsAnalysisPage /> },
        { path: 'ai-summaries', element: <AiSummariesPage /> },
        { path: 'validation-workflow', element: <ValidationWorkflowPage /> },
        { path: 'risk-scoring-config', element: <RiskScoringConfigPage /> },
        { path: 'organizational-context-config', element: <OrganizationalContextConfigPage /> },
        { path: 'domain-leads', element: <DomainLeadsPage /> },
      ],
    },
    {
      path: 'deck',
      element: (
        <Suspense>
          <DeckViewerPage />
        </Suspense>
      ),
    },
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
